import { useQuery } from '@apollo/client';
import { StarIcon as UnfilledStar } from '@heroicons/react/outline';
import { StarIcon as FilledStar } from '@heroicons/react/solid';
import { GetInterestsQuery, GetSkillsQuery, UserUpdateInput } from 'lib/generated/graphql';
import { GET_INTERESTS, GET_SKILLS } from 'lib/queries';
import { useEffect, useState } from 'react';
import Carousel from 'src/screens/onboarding/components/Carousel';
import { Button } from 'src/shared-components';
import Error from 'src/shared-components/Form/Error';

const SkillsInterestsSection = ({
  data = [],
  selectedIds = [],
  title,
  select,
}: {
  data: { id: number; value: string }[];
  selectedIds?: number[];
  title?: string;
  select: (id: number) => void;
}) => {
  return (
    <>
      <div className="font-bold text-left text-sm text-[#868686]">{title}</div>
      <div className="overflow-auto my-3">
        <ul role="list" className="flex flex-wrap gap-2">
          {data?.map(dataObj => {
            const { id, value } = dataObj;
            return (
              <li
                onClick={() => select(id)}
                key={id}
                className={`select-none text-center rounded-lg border cursor-pointer  py-1 px-3 font-medium text-gray-400 ${
                  selectedIds.includes(id) ? 'bg-[#DCD9F7] text-[#4539B2]' : 'bg-white'
                }`}
              >
                {value}
                {selectedIds.includes(id) && <>&nbsp;✔️</>}
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

const HighlightedSkills = ({
  skills = [],
  highlightedSkills = [],
  select,
  selectedSkills = [],
}: {
  skills: { id: number; value: string }[];
  highlightedSkills?: number[];
  select: (id: number) => void;
  selectedSkills: number[];
}) => {
  return (
    <ul role="list" className="overflow-auto h-72 flex items-center flex-col gap-2 my-5">
      {skills
        .filter(skill => selectedSkills.includes(skill.id))
        .map(skill => {
          const { id, value } = skill;
          return (
            <li
              onClick={() => select(id)}
              key={id}
              className="w-fit select-none flex bg-[#DCD9F7] text-[#4539B2] text-center rounded-lg border cursor-pointer  py-1 px-3 font-medium}"
            >
              {highlightedSkills.includes(id) ? (
                <FilledStar className="h-6 w-6 mr-1" />
              ) : (
                <UnfilledStar className="h-6 w-6 mr-1 " />
              )}
              {value}
            </li>
          );
        })}
    </ul>
  );
};

const carouselData = [
  {
    position: 0,
    title: 'Add skills and interests',
    subtitle: 'These help Team Leads match you with great projects and startups on Huddle',
    hideStep: true,
  },
  {
    position: 1,
    title: 'Star your superpowers',
    subtitle: 'Select up to three skills you want to highlight',
    hideStep: true,
  },
];

// *** MAIN COMPONENT
const SkillsInterestsForm = ({
  selectedInterestIds = [],
  selectedSkillIds = [],
  selectedHighlightedSkillIds = [],
  closeModal,
  handleSubmit,
}: {
  selectedInterestIds: number[];
  selectedSkillIds: number[];
  selectedHighlightedSkillIds: number[];
  closeModal: () => void;
  handleSubmit: (data) => void;
}) => {
  // pull the relevant data from the query
  const { data: { findSkills: skills = [] } = {} } = useQuery<GetSkillsQuery>(GET_SKILLS);
  const { data: { findInterests: interests = [] } = {} } = useQuery<GetInterestsQuery>(GET_INTERESTS);

  // create state objects for ids
  const [selectedSkills, setSelectedSkills] = useState<number[]>(selectedSkillIds);
  const [selectedInterests, setSelectedInterests] = useState<number[]>(selectedInterestIds);
  const [selectedHighlightedSkills, setSelectedHighlightedSkills] = useState<number[]>(selectedHighlightedSkillIds);

  // state objects for the searches
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filteredSkills, setFilteredSkills] = useState(skills || []);
  const [filteredInterests, setFilteredInterests] = useState(interests || []);

  useEffect(() => {
    setFilteredInterests(interests || []);
  }, [interests]);
  useEffect(() => {
    setFilteredSkills(skills || []);
  }, [skills]);

  const handleSearch = e => {
    const inputValue = e.target.value;
    setSearchTerm(inputValue);
    setFilteredInterests(interests.filter(interest => interest.value.toLowerCase().includes(inputValue.toLowerCase())));
    setFilteredSkills(skills.filter(skill => skill.value.toLowerCase().includes(inputValue.toLowerCase())));
  };

  // selecting skill or interest
  const selectInterest = (selectedId: number) => {
    if (selectedInterests.includes(selectedId)) setSelectedInterests(selectedInterests.filter(id => selectedId !== id));
    else setSelectedInterests([...selectedInterests, selectedId]);
  };

  const selectSkill = (selectedId: number) => {
    if (selectedSkills.includes(selectedId)) {
      setSelectedSkills(selectedSkills.filter(id => selectedId !== id));
      setSelectedHighlightedSkills(selectedHighlightedSkills.filter(id => selectedId !== id));
    } else setSelectedSkills([...selectedSkills, selectedId]);
  };

  const selectHighlightedSkill = (skillId: number) => {
    if (selectedHighlightedSkills.includes(skillId))
      setSelectedHighlightedSkills(selectedHighlightedSkills.filter(id => skillId !== id));
    else if (selectedHighlightedSkills.length < 3)
      setSelectedHighlightedSkills([...selectedHighlightedSkills, skillId]);
  };

  // transitions
  const [formError, setFormError] = useState<string>();
  const [step, setStep] = useState<number>(0);
  const handleNextButton = () => {
    if (selectedSkills.length === 0) setFormError('Select at least one skill');
    else setStep(1);
  };
  const submitForm = () => {
    const updateData: UserUpdateInput = {
      skills: { set: selectedSkills.map((id: number) => ({ id })) },
      interests: { set: selectedInterests.map((id: number) => ({ id })) },
      highlightedSkills: { set: selectedHighlightedSkills.map((id: number) => ({ id })) },
    };
    handleSubmit(updateData);

    closeModal(); // TODO: where should this be?
  };

  useEffect(() => {
    if (selectedSkills.length > 0) {
      setFormError('');
    }
  }, [selectedSkills]);

  return (
    <Carousel currentStep={step} steps={carouselData} className="sm:w-[450px] overflow-auto">
      <div className="w-full">
        {step === 0 && (
          <div className="screen1">
            <input
              type="text"
              className="bg-gray-200 h-12 w-full border-transparent rounded-lg text-lg mb-5 "
              placeholder="Search..."
              onChange={handleSearch}
              value={searchTerm}
            />
            <div className="h-[250px] mb-4 overflow-auto">
              <SkillsInterestsSection
                title="SKILL"
                data={filteredSkills}
                selectedIds={selectedSkills}
                select={selectSkill}
              />
              <SkillsInterestsSection
                title="INTERESTS"
                data={filteredInterests}
                selectedIds={selectedInterests}
                select={selectInterest}
              />
            </div>

            {formError && <Error error={formError} />}
            <Button size="lg" onClick={handleNextButton} fullWidth={true} color="black">
              Next
            </Button>
          </div>
        )}
        {step === 1 && (
          <div>
            <HighlightedSkills
              skills={skills}
              selectedSkills={selectedSkills}
              highlightedSkills={selectedHighlightedSkills}
              select={selectHighlightedSkill}
            />
            <Button size="lg" fullWidth={true} color="black" onClick={submitForm}>
              Submit
            </Button>
          </div>
        )}
      </div>
    </Carousel>
  );
};

export default SkillsInterestsForm;
